/**
 * AppDeprovisionModal — full delete only.
 *
 * O modo dns_only ("Tirar do ZTNA, reversível") foi promovido ao
 * SegmentedControl do AppDrawer (modo Aberto). Este modal só executa
 * a exclusão definitiva (full): reverte DNS, deleta ALB rule, remove cert
 * exclusivo e faz hard DELETE na row applications.
 *
 * Double-confirm obrigatório: user deve digitar o hostname exato antes
 * do botão "Excluir definitivamente" ficar habilitado.
 */
import { useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import { foiParaGmud, notifyPendingGmud } from '../gmudNotice'
import type { AppHealthSummary } from '../types'
import { formatApiError } from '@/api/errors'

interface Props {
  app: AppHealthSummary
  opened: boolean
  onClose: () => void
}

export function AppDeprovisionModal({ app, opened, onClose }: Props) {
  const queryClient = useQueryClient()

  const [confirmText, setConfirmText] = useState('')

  const canSubmit = confirmText === app.hostname

  const deprovisionMutation = useMutation({
    mutationFn: () =>
      ztnaApi.deprovisionApp(app.id, {
        mode: 'full',
        confirm_hostname: app.hostname,
      }),
    onSuccess: (response) => {
      // A pior mensagem do conjunto se ficasse sem guarda: anunciava "App
      // removida" e descrevia a destruicao de DNS, ALB rule e certificado —
      // nenhuma delas acontece enquanto a GMUD nao for aprovada. Um operador
      // lendo isso concluiria que a app ja nao existe mais.
      if (foiParaGmud(response)) {
        notifyPendingGmud('a exclusão', app.hostname, response.gmud_id)
        onClose()
        return
      }
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({
        queryKey: ['ztna', 'app-audit-events', app.id],
      })
      notifications.show({
        title: 'Aplicação removida',
        message: `Job ${response.job_id} disparado: DNS reverte, ALB rule deletada, cert removido se exclusivo, row deletada.`,
        color: 'orange',
      })
      onClose()
    },
    onError: (err: unknown) => {
      // O 422 `no_provision_history` chega com `detail` OBJETO. A leitura
      // anterior (`typeof detail === 'string' ? detail : null`) descartava a
      // explicação e mostrava o texto do axios.
      notifications.show({
        title: 'Falha ao iniciar a exclusão',
        message: formatApiError(err, 'Falha ao iniciar a exclusão.'),
        color: 'red',
      })
    },
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Excluir ${app.slug}`}
      size="lg"
    >
      <Stack gap="md">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Ação irreversível"
        >
          <Stack gap={6}>
            <Text size="sm">
              DNS reverte; ALB rule deletada; cert deletado se exclusivo;
              row de applications deletada. Audit histórico preservado.
            </Text>
            <Text size="sm">
              Para confirmar, digite o hostname exato:
            </Text>
            <Text size="xs" ff="JetBrains Mono, monospace" c="dimmed">
              {app.hostname}
            </Text>
            {/* `ff` no TextInput do Mantine cai no WRAPPER, nao no <input>: medido no
                navegador em 31/08/2026, o computed do input era `Barlow, -apple-system, …`.
                O campo existe para comparar o hostname caractere por caractere com a
                referencia acima, que esta em JetBrains Mono — em duas tipografias
                diferentes a comparacao fica mais dificil justamente onde ela importa.
                `styles.input` acerta o elemento certo. */}
            <TextInput
              value={confirmText}
              onChange={(e) => setConfirmText(e.currentTarget.value)}
              placeholder={app.hostname}
              styles={{ input: { fontFamily: '"JetBrains Mono", monospace' } }}
            />
          </Stack>
        </Alert>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={() => deprovisionMutation.mutate()}
            loading={deprovisionMutation.isPending}
            disabled={!canSubmit}
          >
            Excluir definitivamente
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default AppDeprovisionModal
